/**
 * A crown object's tile archive (`X.pmtiles` beside its `X.gpkg`), when it has one: the map
 * draws its layers straight from it, and the stems the page counts are decoded from its
 * coarsest tiles — so neither GeoPackage is downloaded. Without one, the page reads the
 * GeoPackage as before.
 */
import { useEffect, useState } from 'react';
import type { Feature, FeatureCollection } from 'geojson';
import { VectorTile } from '@mapbox/vector-tile';
import { PbfReader } from 'pbf';
import type { LinkSource } from '@/map/linkSource';
import { openPmtiles, type Archive } from '@/map/pmtiles';

export type ArchiveState = { state: 'off' | 'checking' | 'none'; archive?: undefined } | { state: 'ready'; archive: Archive };

/** Opens the archive beside `gpkg` once `enabled`; `none` when there is no such file (or it cannot be read). */
export function useArchive(object: string | undefined, gpkg: string | undefined, links: LinkSource, enabled: boolean): ArchiveState {
  const [state, setState] = useState<{ key: string; archive: Archive | null } | null>(null);
  const key = object && gpkg && enabled ? `${object}\u0000${gpkg}` : '';
  useEffect(() => {
    if (!key) return;
    const [o, g] = key.split('\u0000') as [string, string];
    let alive = true;
    void openPmtiles(o, g, links).then((archive) => alive && setState({ key, archive }));
    return () => {
      alive = false;
    };
  }, [key, links]);
  if (!key) return { state: 'off' };
  if (!state || state.key !== key) return { state: 'checking' };
  return state.archive ? { state: 'ready', archive: state.archive } : { state: 'none' };
}

const lon2tile = (lon: number, z: number) => Math.floor(((lon + 180) / 360) * 2 ** z);
const lat2tile = (lat: number, z: number) => {
  const r = (lat * Math.PI) / 180;
  return Math.floor(((1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2) * 2 ** z);
};

/** More tiles than this at the zoom read and the layer is not read whole. */
const MAX_TILES = 2048;
/** Tiles asked for at once. */
const PARALLEL_TILES = 16;

/**
 * Every feature of one source layer, read from the archive's coarsest zoom over its bounds —
 * where an MLS archive keeps all its stems in a tile or two — or from `zoom`. Each feature
 * keeps its tile id (the stem's `id`) as the GeoJSON id.
 */
export async function readLayer(archive: Archive, layer: string, zoom?: number): Promise<FeatureCollection> {
  const { header, tiles } = archive;
  const z = zoom ?? header.minZoom;
  const [x0, x1] = [lon2tile(header.minLon, z), lon2tile(header.maxLon, z)];
  const [y0, y1] = [lat2tile(header.maxLat, z), lat2tile(header.minLat, z)];
  if ((x1 - x0 + 1) * (y1 - y0 + 1) > MAX_TILES) throw new Error(`too many tiles at z${z}`);
  const coords: [number, number][] = [];
  for (let x = x0; x <= x1; x++) for (let y = y0; y <= y1; y++) coords.push([x, y]);
  const features: Feature[] = [];
  const seen = new Set<number>();
  for (let i = 0; i < coords.length; i += PARALLEL_TILES) {
    const batch = coords.slice(i, i + PARALLEL_TILES);
    const read = await Promise.all(batch.map(([x, y]) => tiles.getZxy(z, x, y)));
    read.forEach((tile, j) => {
      if (!tile) return;
      const [x, y] = batch[j]!;
      const vt = new VectorTile(new PbfReader(new Uint8Array(tile.data)));
      const source = vt.layers[layer];
      for (let k = 0; k < (source?.length ?? 0); k++) {
        const f = source!.feature(k);
        // A feature on a tile edge may be in two tiles.
        if (f.id !== undefined && seen.has(f.id)) continue;
        if (f.id !== undefined) seen.add(f.id);
        const g = f.toGeoJSON(x, y, z);
        features.push({ ...g, id: f.id ?? g.id });
      }
    });
  }
  return { type: 'FeatureCollection', features };
}

/** How many tiles cover the archive's bounds at `z`. */
export function tilesAt(archive: Archive, z: number): number {
  const { header } = archive;
  return (lon2tile(header.maxLon, z) - lon2tile(header.minLon, z) + 1) * (lat2tile(header.minLat, z) - lat2tile(header.maxLat, z) + 1);
}

export interface ArchiveLayer {
  fc: FeatureCollection | null;
  loading: boolean;
  failed: boolean;
}

/** One source layer of an archive, read once per archive. */
export function useArchiveLayer(archive: Archive | undefined, layer: string): ArchiveLayer {
  const [state, setState] = useState<{ archive: Archive; fc: FeatureCollection | null; failed: boolean } | null>(null);
  useEffect(() => {
    if (!archive) return;
    let alive = true;
    readLayer(archive, layer).then(
      (fc) => alive && setState({ archive, fc, failed: false }),
      () => alive && setState({ archive, fc: null, failed: true }),
    );
    return () => {
      alive = false;
    };
  }, [archive, layer]);
  if (!archive) return { fc: null, loading: false, failed: false };
  if (!state || state.archive !== archive) return { fc: null, loading: true, failed: false };
  return { fc: state.fc, loading: false, failed: state.failed };
}
